import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Pie } from 'react-chartjs-2'
import {
  CircularProgress,
  Box,
  useTheme,
  Typography,
  Divider,
} from '@mui/material'
import supabase from '../supabaseClient'
import dayjs from 'dayjs';
import { isAfter, subWeeks, subMonths, subYears } from 'date-fns';
import { calculateProgress } from '../utils/quranUtils';
import TimePeriodFilter from './ui/TimePeriodFilter';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const StudentQuranProgressChart = ({ studentId, progressMode = 'verses' }) => {
  const [progressData, setProgressData] = useState([])
  const [detailedData, setDetailedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('month')
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const theme = useTheme()

  // Handle view mode change
  const handleViewModeChange = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue)
    }
  }

  // Handle bar click to show detailed breakdown
  const handleBarClick = useCallback(
    (event, elements) => {
      if (elements.length > 0) {
        const { index } = elements[0]
        const period = progressData[index].period
        setSelectedPeriod(period)
      }
    },
    [progressData],
  )

  useEffect(() => {
    const fetchStudentQuranProgress = async () => {
      if (!studentId) return

      try {
        setLoading(true)

        // Fetch all Quran progress data for this student (no time filter)
        const { data, error } = await supabase
          .from('student_quran_progress')
          .select(
            `
            *,
            classes(name)
          `,
          )
          .eq('student_id', studentId)
          .order('date', { ascending: true })

        if (error) {
          console.error('Error fetching student Quran progress:', error)
          return
        }

        // Process data for chart
        const processedData = []
        let cumulativeProgress = 0

        // Group by month or year depending on view mode
        const groupedData = {}

        // Apply time filter based on viewMode
        const filteredData = data.filter((record) => {
          const recordDate = new Date(record.date)
          const now = new Date()

          if (viewMode === 'week') {
            return isAfter(recordDate, subWeeks(now, 1))
          } else if (viewMode === 'month') {
            return isAfter(recordDate, subMonths(now, 1))
          } else if (viewMode === '6months') {
            return isAfter(recordDate, subMonths(now, 6))
          } else if (viewMode === 'year') {
            return isAfter(recordDate, subYears(now, 1))
          }
          return true // No filter for other modes
        })

        // Store detailed data for pie chart
        setDetailedData(filteredData)

        filteredData.forEach((record) => {
          const date = dayjs(record.date)
          // Use view mode to determine grouping
          const groupKey =
            viewMode === 'month' ? date.format('MMM YYYY') : date.format('YYYY')

          const progress = calculateProgress(
            record.starting_surah,
            record.starting_verse,
            record.ending_surah,
            record.ending_verse,
            progressMode,
          )

          if (!groupedData[groupKey]) {
            groupedData[groupKey] = 0
          }

          groupedData[groupKey] += progress
        })

        // Convert grouped data to array for chart
        // Sort by date for proper chronological display
        const sortedEntries = Object.entries(groupedData).sort((a, b) => {
          // For month view: Parse "MMM YYYY" format
          if (viewMode === 'month') {
            return (
              dayjs(a[0], 'MMM YYYY').valueOf() -
              dayjs(b[0], 'MMM YYYY').valueOf()
            )
          }
          // For year view: Compare year strings directly
          return a[0].localeCompare(b[0])
        })

        sortedEntries.forEach(([period, progress]) => {
          cumulativeProgress += progress
          processedData.push({
            period,
            progress,
            cumulativeProgress,
          })
        })

        setProgressData(processedData)
      } catch (error) {
        console.error('Error in fetchStudentQuranProgress:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStudentQuranProgress()
  }, [studentId, viewMode, progressMode])

  // Get unit label based on progress mode
  const unitLabel = progressMode === 'pages' ? 'Pages Completed' : 'Verses Completed'
  const unitName = progressMode === 'pages' ? 'pages' : 'verses'

  // Prepare chart data
  const chartData = {
    labels: progressData.map((item) => item.period),
    datasets: [
      {
        label: unitLabel,
        data: progressData.map((item) => item.progress),
        backgroundColor: progressData.map((_, index) => {
          // Generate different colors for each bar
          const colorPalette = [
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(231, 233, 237, 0.6)',
            'rgba(156, 39, 176, 0.6)',
            'rgba(33, 150, 243, 0.6)',
            'rgba(76, 175, 80, 0.6)',
            'rgba(255, 152, 0, 0.6)',
            'rgba(121, 85, 72, 0.6)',
          ]
          return colorPalette[index % colorPalette.length]
        }),
        borderColor: progressData.map((_, index) => {
          // Generate matching border colors
          const borderPalette = [
            'rgb(75, 192, 192)',
            'rgb(153, 102, 255)',
            'rgb(255, 159, 64)',
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(255, 206, 86)',
            'rgb(231, 233, 237)',
            'rgb(156, 39, 176)',
            'rgb(33, 150, 243)',
            'rgb(76, 175, 80)',
            'rgb(255, 152, 0)',
            'rgb(121, 85, 72)',
          ]
          return borderPalette[index % borderPalette.length]
        }),
        borderWidth: 1,
      },
    ],
  }

  // Chart options with theme-aware styling
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleBarClick,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: `Quran Progress by ${unitName.charAt(0).toUpperCase() + unitName.slice(1)} (${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)})`,
        color: theme.palette.text.primary,
        font: {
          family: theme.typography.fontFamily,
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
        titleColor:
          theme.palette.mode === 'dark'
            ? theme.palette.text.primary
            : theme.palette.text.primary,
        bodyColor:
          theme.palette.mode === 'dark'
            ? theme.palette.text.primary
            : theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
      y: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
  }

  // Chart container style
  const chartContainerStyle = {
    height: 300,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  }

  // Prepare data for the pie chart (class breakdown)
  const pieChartData = useMemo(() => {
    if (!selectedPeriod) return null

    // Filter records by selected period
    const filteredRecords = detailedData.filter((record) => {
      const date = dayjs(record.date)
      const recordPeriod =
        viewMode === 'month' ? date.format('MMM YYYY') : date.format('YYYY')
      return recordPeriod === selectedPeriod
    })

    // Group by class name
    const classCounts = {}
    filteredRecords.forEach((record) => {
      const className = record.classes?.name || `Class ID: ${record.class_id}`
      const progress = calculateProgress(
        record.starting_surah,
        record.starting_verse,
        record.ending_surah,
        record.ending_verse,
        progressMode,
      )
      classCounts[className] = (classCounts[className] || 0) + progress
    })

    // Use consistent color palette
    const backgroundColor = [
      'rgba(255, 99, 132, 0.2)',
      'rgba(255, 159, 64, 0.2)',
      'rgba(255, 205, 86, 0.2)',
      'rgba(75, 192, 192, 0.2)',
      'rgba(54, 162, 235, 0.2)',
      'rgba(153, 102, 255, 0.2)',
      'rgba(201, 203, 207, 0.2)',
    ]

    const borderColor = [
      'rgb(255, 99, 132)',
      'rgb(255, 159, 64)',
      'rgb(255, 205, 86)',
      'rgb(75, 192, 192)',
      'rgb(54, 162, 235)',
      'rgb(153, 102, 255)',
      'rgb(201, 203, 207)',
    ]

    // If we have more classes than colors, repeat the colors
    const classCount = Object.keys(classCounts).length
    const extendedBackgroundColors = Array(classCount)
      .fill()
      .map((_, i) => backgroundColor[i % backgroundColor.length])

    const extendedBorderColors = Array(classCount)
      .fill()
      .map((_, i) => borderColor[i % borderColor.length])

    return {
      labels: Object.keys(classCounts),
      datasets: [
        {
          data: Object.values(classCounts),
          backgroundColor: extendedBackgroundColors,
          borderColor: extendedBorderColors,
          borderWidth: 1,
        },
      ],
    }
  }, [selectedPeriod, detailedData, viewMode, progressMode])

  // Pie chart options
  const pieChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: theme.palette.text.primary,
            font: {
              family: theme.typography.fontFamily,
              size: 12,
            },
          },
        },
        title: {
          display: true,
          text: `Quran Progress Breakdown by Class (${selectedPeriod})`,
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 16,
            weight: 'bold',
          },
        },
        tooltip: {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          titleColor:
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : theme.palette.text.primary,
          bodyColor:
            theme.palette.mode === 'dark'
              ? theme.palette.text.primary
              : theme.palette.text.primary,
          borderColor: theme.palette.divider,
          borderWidth: 1,
        },
      },
    }),
    [selectedPeriod, theme],
  )

  if (loading) {
    return (
      <Box sx={chartContainerStyle}>
        <CircularProgress size={40} thickness={4} />
      </Box>
    )
  }

  return (
    <Box>
      <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />

      <Box sx={chartContainerStyle}>
        <Bar options={chartOptions} data={chartData} />
      </Box>

      {selectedPeriod && pieChartData && (
        <>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Divider>
              <Typography variant="body2" color="text.secondary">
                Click on a bar above to see class breakdown
              </Typography>
            </Divider>
          </Box>

          <Box sx={chartContainerStyle}>
            <Pie options={pieChartOptions} data={pieChartData} />
          </Box>

          {/* Detailed progress list */}
          <Box sx={{ mt: 3, px: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Quran progress details for {selectedPeriod}:
            </Typography>
            <Box
              sx={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
                p: 2,
              }}
            >
              {detailedData
                .filter((record) => {
                  const date = dayjs(record.date)
                  const recordPeriod =
                    viewMode === 'month'
                      ? date.format('MMM YYYY')
                      : date.format('YYYY')
                  return recordPeriod === selectedPeriod
                })
                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date, newest first
                .map((record, index) => {
                  const progress = calculateProgress(
                    record.starting_surah,
                    record.starting_verse,
                    record.ending_surah,
                    record.ending_verse,
                    progressMode,
                  )
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                        borderBottom:
                          index <
                          detailedData.filter((r) => {
                            const date = dayjs(r.date)
                            const recordPeriod =
                              viewMode === 'month'
                                ? date.format('MMM YYYY')
                                : date.format('YYYY')
                            return recordPeriod === selectedPeriod
                          }).length -
                            1
                            ? `1px solid ${theme.palette.divider}`
                            : 'none',
                      }}
                    >
                      <Typography variant="body2">
                        {dayjs(record.date).format('MMM D, YYYY')} - {progress}{' '}
                        {unitName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.classes?.name || `Class ID: ${record.class_id}`}
                      </Typography>
                    </Box>
                  )
                })}
              {detailedData.filter((record) => {
                const date = dayjs(record.date)
                const recordPeriod =
                  viewMode === 'month'
                    ? date.format('MMM YYYY')
                    : date.format('YYYY')
                return recordPeriod === selectedPeriod
              }).length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 1 }}
                >
                  No detailed records found.
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      {!selectedPeriod && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Click on a bar above to see class breakdown
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default StudentQuranProgressChart
